import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-text'
import 'ace-builds/src-noconflict/theme-xcode'
import SydButton from '../../../../../../../commonDesign/Button'
import { computeLineDiff } from './diffUtils'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: 'var(--border-radius-lg)',
    width: '92vw',
    height: '85vh',
    maxWidth: '1400px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0,
    gap: 'var(--gap-md)'
  },
  headerTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: 'var(--color-neutral-900)'
  },
  reasoning: {
    fontSize: '13px',
    color: 'var(--color-neutral-500)',
    marginLeft: '12px',
    fontWeight: 400,
    fontStyle: 'italic'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'var(--color-neutral-400)',
    padding: '4px',
    '&:hover': { color: 'var(--color-neutral-600)' }
  },
  body: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden'
  },
  pane: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    '&:first-child': {
      borderRight: '1px solid var(--color-neutral-200)'
    }
  },
  paneHeader: {
    padding: '6px 16px',
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  paneHeaderOld: {
    backgroundColor: 'var(--color-danger-100)',
    color: 'var(--color-danger-700)'
  },
  paneHeaderNew: {
    backgroundColor: 'var(--color-success-100)',
    color: 'var(--color-success-700)'
  },
  editorWrapper: {
    flex: 1,
    overflow: 'hidden',
    '& .ace_editor': {
      fontFamily: 'var(--font-mono, monospace) !important'
    },
    '& .diff-line-removed': {
      position: 'absolute',
      backgroundColor: 'rgba(239, 68, 68, 0.18) !important',
      borderLeft: '3px solid var(--color-danger-400)'
    },
    '& .diff-line-added': {
      position: 'absolute',
      backgroundColor: 'rgba(34, 197, 94, 0.18) !important',
      borderLeft: '3px solid var(--color-success-500)'
    },
    '& .diff-gutter-removed': {
      backgroundColor: 'rgba(239, 68, 68, 0.25) !important',
      color: 'var(--color-danger-600) !important'
    },
    '& .diff-gutter-added': {
      backgroundColor: 'rgba(34, 197, 94, 0.25) !important',
      color: 'var(--color-success-700) !important'
    }
  },
  metricGrid: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
    display: 'grid',
    gridTemplateColumns: '160px 1fr 1fr',
    gap: '12px 24px',
    alignItems: 'start',
    fontSize: '13px',
    color: 'var(--color-neutral-800)'
  },
  metricGridHeader: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    paddingBottom: '6px',
    borderBottom: '1px solid var(--color-neutral-200)'
  },
  metricGridHeaderField: {
    color: 'var(--color-neutral-500)'
  },
  metricGridHeaderOld: {
    color: 'var(--color-danger-700)'
  },
  metricGridHeaderNew: {
    color: 'var(--color-success-700)'
  },
  metricFieldLabel: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '12px',
    color: 'var(--color-neutral-500)',
    fontWeight: 600
  },
  metricCell: {
    padding: '6px 10px',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--color-neutral-050)',
    minHeight: '24px',
    wordBreak: 'break-word'
  },
  metricCellEmpty: {
    color: 'var(--color-neutral-400)',
    fontStyle: 'italic'
  },
  metricCellChanged: {
    backgroundColor: 'rgba(34, 197, 94, 0.12)',
    border: '1px solid var(--color-success-300)'
  },
  metricCellRemoved: {
    backgroundColor: 'rgba(239, 68, 68, 0.10)',
    color: 'var(--color-neutral-400)',
    textDecoration: 'line-through'
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px',
    borderTop: '1px solid var(--color-neutral-200)',
    flexShrink: 0
  },
  stats: {
    fontSize: '12px',
    color: 'var(--color-neutral-500)',
    display: 'flex',
    gap: '12px'
  },
  statAdded: { color: 'var(--color-success-600)', fontWeight: 600 },
  statRemoved: { color: 'var(--color-danger-600)', fontWeight: 600 },
  actions: {
    display: 'flex',
    gap: '8px'
  }
}))

function applyMarkers (editor, rows, lineClass, gutterClass) {
  if (!editor) return
  const session = editor.getSession()
  session.clearBreakpoints()
  for (const row of rows) {
    session.addGutterDecoration(row, gutterClass)
  }
  const Range = window.ace?.require('ace/range')?.Range
  if (Range) {
    for (const row of rows) {
      session.addMarker(new Range(row, 0, row, Infinity), lineClass, 'fullLine', false)
    }
  }
}

function DiffEditor ({ source, markerRows, lineClass, gutterClass, editorRef, onScroll }) {
  const classes = useStyles()

  const handleLoad = useCallback((editor) => {
    editorRef.current = editor
    applyMarkers(editor, markerRows, lineClass, gutterClass)
    editor.getSession().on('changeScrollTop', (scrollTop) => {
      if (onScroll) onScroll(scrollTop)
    })
    editor.getSession().on('changeScrollLeft', (scrollLeft) => {
      if (onScroll) onScroll(null, scrollLeft)
    })
  }, [markerRows, lineClass, gutterClass, editorRef, onScroll])

  return (
    <div className={classes.editorWrapper}>
      <AceEditor
        mode='text'
        theme='xcode'
        value={source}
        readOnly
        highlightActiveLine={false}
        showGutter
        showPrintMargin={false}
        width='100%'
        height='100%'
        fontSize={12}
        tabSize={2}
        wrapEnabled
        editorProps={{ $blockScrolling: true }}
        setOptions={{ useWorker: false, showFoldWidgets: false }}
        onLoad={handleLoad}
      />
    </div>
  )
}

DiffEditor.propTypes = {
  source: PropTypes.string.isRequired,
  markerRows: PropTypes.array.isRequired,
  lineClass: PropTypes.string.isRequired,
  gutterClass: PropTypes.string.isRequired,
  editorRef: PropTypes.object.isRequired,
  onScroll: PropTypes.func
}

function TextDiffBody ({ current, proposed }) {
  const classes = useStyles()
  const oldRef = useRef(null)
  const newRef = useRef(null)
  const syncing = useRef(false)

  const diff = useMemo(() => computeLineDiff(current, proposed), [current, proposed])

  const handleOldScroll = useCallback((scrollTop, scrollLeft) => {
    if (syncing.current) return
    syncing.current = true
    const target = newRef.current
    if (target) {
      if (scrollTop != null) target.getSession().setScrollTop(scrollTop)
      if (scrollLeft != null) target.getSession().setScrollLeft(scrollLeft)
    }
    requestAnimationFrame(() => { syncing.current = false })
  }, [])

  const handleNewScroll = useCallback((scrollTop, scrollLeft) => {
    if (syncing.current) return
    syncing.current = true
    const target = oldRef.current
    if (target) {
      if (scrollTop != null) target.getSession().setScrollTop(scrollTop)
      if (scrollLeft != null) target.getSession().setScrollLeft(scrollLeft)
    }
    requestAnimationFrame(() => { syncing.current = false })
  }, [])

  return (
    <>
      <div className={classes.body}>
        <div className={classes.pane}>
          <div className={`${classes.paneHeader} ${classes.paneHeaderOld}`}>Current</div>
          <DiffEditor
            source={current || ''}
            markerRows={diff.removedRows}
            lineClass='diff-line-removed'
            gutterClass='diff-gutter-removed'
            editorRef={oldRef}
            onScroll={handleOldScroll}
          />
        </div>
        <div className={classes.pane}>
          <div className={`${classes.paneHeader} ${classes.paneHeaderNew}`}>Proposed</div>
          <DiffEditor
            source={proposed || ''}
            markerRows={diff.addedRows}
            lineClass='diff-line-added'
            gutterClass='diff-gutter-added'
            editorRef={newRef}
            onScroll={handleNewScroll}
          />
        </div>
      </div>
      <div className={classes.stats}>
        <span className={classes.statAdded}>+{diff.added} added</span>
        <span className={classes.statRemoved}>{'\u2212'}{diff.removed} removed</span>
      </div>
    </>
  )
}

TextDiffBody.propTypes = {
  current: PropTypes.string,
  proposed: PropTypes.string
}

const METRIC_FIELDS = [
  { key: 'name', label: 'name' },
  { key: 'type', label: 'type' },
  { key: 'description', label: 'description' },
  { key: 'weight', label: 'weight' }
]

function renderField (value, classes, opts = {}) {
  const empty = value === undefined || value === null || value === ''
  const cellClasses = [classes.metricCell]
  if (empty) cellClasses.push(classes.metricCellEmpty)
  if (opts.changed) cellClasses.push(classes.metricCellChanged)
  if (opts.removed) cellClasses.push(classes.metricCellRemoved)
  return (
    <div className={cellClasses.join(' ')}>
      {empty ? '\u2014' : String(value)}
    </div>
  )
}

function MetricDiffBody ({ current, proposed, mode }) {
  const classes = useStyles()
  const isRemove = mode === 'remove'

  return (
    <div className={classes.metricGrid}>
      <div className={`${classes.metricGridHeader} ${classes.metricGridHeaderField}`}>Field</div>
      <div className={`${classes.metricGridHeader} ${classes.metricGridHeaderOld}`}>Current</div>
      <div className={`${classes.metricGridHeader} ${classes.metricGridHeaderNew}`}>
        {isRemove ? 'After remove' : 'Proposed'}
      </div>

      {METRIC_FIELDS.map(({ key, label }) => {
        const oldVal = current?.[key]
        const newVal = isRemove ? undefined : proposed?.[key]
        const changed = !isRemove && current && oldVal !== newVal
        return (
          <React.Fragment key={key}>
            <div className={classes.metricFieldLabel}>{label}</div>
            {renderField(oldVal, classes, { removed: isRemove && oldVal !== undefined })}
            {renderField(newVal, classes, { changed })}
          </React.Fragment>
        )
      })}
    </div>
  )
}

MetricDiffBody.propTypes = {
  current: PropTypes.object,
  proposed: PropTypes.object,
  mode: PropTypes.oneOf(['upsert', 'remove']).isRequired
}

function ProposalReviewModal ({
  flavor,
  title,
  reasoning,
  textCurrent,
  textProposed,
  metricCurrent,
  metricProposed,
  metricMode,
  onAccept,
  onReject
}) {
  const classes = useStyles()

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onReject() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onReject])

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onReject()
  }

  return (
    <div className={classes.overlay} onClick={handleOverlayClick}>
      <div className={classes.modal}>
        <div className={classes.header}>
          <div>
            <span className={classes.headerTitle}>{title}</span>
            {reasoning && <span className={classes.reasoning}>{reasoning}</span>}
          </div>
          <button
            className={classes.closeBtn}
            onClick={onReject}
            aria-label='Close review modal'
          >
            {'\u00D7'}
          </button>
        </div>

        {flavor === 'text' && (
          <TextDiffBody current={textCurrent} proposed={textProposed} />
        )}
        {flavor === 'metric' && (
          <MetricDiffBody current={metricCurrent} proposed={metricProposed} mode={metricMode} />
        )}

        <div className={classes.footer}>
          <div />
          <div className={classes.actions}>
            <SydButton size='sm' variant='outline' onClick={onReject}>
              Reject
            </SydButton>
            <SydButton size='sm' variant='ai' onClick={onAccept}>
              Accept
            </SydButton>
          </div>
        </div>
      </div>
    </div>
  )
}

ProposalReviewModal.propTypes = {
  flavor: PropTypes.oneOf(['text', 'metric']).isRequired,
  title: PropTypes.string.isRequired,
  reasoning: PropTypes.string,
  textCurrent: PropTypes.string,
  textProposed: PropTypes.string,
  metricCurrent: PropTypes.object,
  metricProposed: PropTypes.object,
  metricMode: PropTypes.oneOf(['upsert', 'remove']),
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired
}

export default ProposalReviewModal
